import React from 'react';
import PropTypes from 'prop-types';

import TouchBlocker from './TouchBlocker';
import utils from '../../../../common/utils';
import fabric from '../../../../common/fabric';
import { Matrix } from '../../../../common/lib/matrix';

/**
 * Shared iframe component for rich block. It can be passed a preview image url
 * that will be displayed until the iframe content is loaded
 */
const Iframe = props => {
  const { params, matrixGroup, globalScale } = props;
  const { shadow } = params;
  // iframes have non scaled size so we must recompute the correct non scaledMatrix

  // we have to render the sketchfab at the smaller size possible for better performance (and not crashing on low end devices)
  // we apply the actual size thus have to revert the scale from the transform operations via the matrix lib
  const groupMatrix = matrixGroup
    ? fabric.util.multiplyTransformMatrices(
        matrixGroup,
        params.ludus.transformMatrix,
      )
    : params.ludus.transformMatrix;

  const transformMatrix = fabric.util.multiplyTransformMatrices(
    [globalScale, 0, 0, globalScale, 0, 0],
    groupMatrix,
  );
  const options = fabric.util.qrDecompose(transformMatrix);
  // we have to render the iframes at the smallest size possible for better performance (and not crashing on low end device)
  // we apply the actual size thus have to revert the scale from the transform operations via the matrix lib

  const matrix = new Matrix(transformMatrix);
  matrix.scale(1 / options.scaleX, 1 / options.scaleY);
  const opts = matrix.decompose();
  let width = params.width * options.scaleX;
  let height = params.height * options.scaleY;
  let scale = 1;
  const MIN_WIDTH = 600;
  let transform = 'none';

  // on most iframes we can apply a min scale to get a better UX especially on mobile
  // but some blocks are not scalable and would crash on iOS even if we apply a scale of 1, it concernes all intensive webgl-based iframes (ie: sketchfab and figma)
  if (width < MIN_WIDTH && props.isScalable) {
    const targetWidth = params.width * options.scaleX;
    width = MIN_WIDTH;
    scale = targetWidth / MIN_WIDTH;
    height /= scale;
    transform = `translate(-50%, -50%) translate(${options.translateX}px, ${options.translateY}px) scale(${scale})  rotate(${options.angle}deg)  skew(${opts.skew.x}rad, ${options.skewY}deg)`;
  } else {
    transform = `translate(-50%, -50%) translate(${options.translateX}px, ${options.translateY}px) rotate(${options.angle}deg)  skew(${opts.skew.x}rad, ${options.skewY}deg)`;
  }

  const styles = {
    ...props.style,
    width: width,
    height: height,
    left: 0,
    top: 0,
    transformOrigin: 'center',
    overflow: 'visible',
    transform: 'none',
  };

  const blockStyles = {
    opacity: props.groupOpacity,
    backgroundColor: 'transparent',
    position: 'absolute',
    transform,
    width: width,
    height: height,
    left: 0,
    top: 0,
    boxShadow: shadow
      ? utils.getDropShadowCSSFilterFromFabricShadow(
          shadow,
          options.angle,
          1 / options.scaleX,
          true,
        )
      : 'none',
  };

  const extraProps = props.sandboxAllow
    ? { sandbox: 'allow-forms allow-same-origin allow-scripts' }
    : {};

  return (
    <TouchBlocker
      style={blockStyles}
      enabled={utils.isMobileDevice()}
      windowHasFocus={props.windowHasFocus}
      deactivateOnClick
    >
      <div>
        <iframe
          title={props.title}
          src={props.src}
          style={styles}
          frameBorder="0"
          tabIndex="-1"
          allowFullScreen
          scrolling={props.scrolling ? 'yes' : 'no'}
          {...extraProps}
        />
      </div>
    </TouchBlocker>
  );
};

Iframe.propTypes = {
  title: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  transitionStatus: PropTypes.string.isRequired,
  style: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  applyScaleFix: PropTypes.bool.isRequired,
  globalScale: PropTypes.number.isRequired,
  windowHasFocus: PropTypes.bool.isRequired,
  groupOpacity: PropTypes.number,
  groupAngle: PropTypes.number.isRequired,
  groupScaleX: PropTypes.number.isRequired,
  groupScaleY: PropTypes.number.isRequired,
  sandboxAllow: PropTypes.bool,
  showLoader: PropTypes.bool,
  scrolling: PropTypes.bool,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
  isScalable: PropTypes.bool,
};
Iframe.defaultProps = {
  groupOpacity: 1,
  sandboxAllow: false,
  showLoader: true,
  scrolling: true,
  matrixGroup: null,
  isScalable: true,
};

export default Iframe;
